// src/pages/ResetPassword.jsx
import React, { useMemo, useState } from "react";
import axios from "../services/api";
import { Link, useSearchParams } from "react-router-dom";

const validatePassword = (pw) => ({
  length: pw.length >= 8 && pw.length <= 72,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  digit: /\d/.test(pw),
  special: /[^A-Za-z0-9]/.test(pw),
});

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => validatePassword(pw1), [pw1]);
  const pwValid = Object.values(rules).every(Boolean);
  const pwMatch = pw2.length > 0 && pw1 === pw2;

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");

    if (!pwValid) return setErr("Password must meet all the requirements below.");
    if (!pwMatch) return setErr("Passwords do not match.");

    setLoading(true);
    try {
      await axios.post("/api/auth/reset-password", { token, newPassword: pw1 });
      setMsg("Password updated. You can now log in.");
      setPw1("");
      setPw2("");
    } catch (e) {
        const data = e?.response?.data;
        const text =
        typeof data === "string"
          ? data
          : data?.message ||
            data?.error ||
            (Array.isArray(data?.errors) ? data.errors.map(x => x.message || String(x)).join(", ") : null) ||
            "Reset failed.";
      setErr(text);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="page">
        <section className="card bg-red-500/10 border-red-500/20 text-red-200">
          Missing or invalid reset link. Please request a new email.
        </section>
      </div>
    );
  }

  return (
    <div className="page max-w-md space-y-6">
      <section className="card">
        <h1 className="text-2xl font-semibold text-white">Set a new password</h1>
        <p className="mt-1 text-slate-300 text-sm">
          Choose a strong password you haven’t used elsewhere.
        </p>

        {/* Alerts */}
        {msg && (
          <div className="mt-4 rounded border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-emerald-200" aria-live="polite">
            {msg}{" "}
            <Link to="/login" className="underline font-medium">
              Go to login
            </Link>
            .
          </div>
        )}
        {err && (
          <div className="mt-4 rounded border border-red-500/20 bg-red-500/10 px-3 py-2 text-red-200" aria-live="assertive">
            {err}
          </div>
        )}

        {/* Form */}
        <form onSubmit={submit} className="mt-4 space-y-4">
          {/* New password */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">New password</label>
            <div className="relative">
              <input
                type={show1 ? "text" : "password"}
                className={`input pr-24 ${pw1 && !pwValid ? "ring-1 ring-red-500/40" : ""}`}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                autoComplete="new-password"
                maxLength={72}
                required
              />
              <button
                type="button"
                onClick={() => setShow1((v) => !v)}
                className="absolute inset-y-0 right-0 my-1 mr-1 rounded px-3 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20"
                aria-label={show1 ? "Hide password" : "Show password"}
              >
                {show1 ? "Hide" : "Show"}
              </button>
            </div>

            <ul className="mt-2 text-xs space-y-1">
              <Rule ok={rules.length} text="8–72 characters" />
              <Rule ok={rules.upper} text="At least one uppercase letter" />
              <Rule ok={rules.lower} text="At least one lowercase letter" />
              <Rule ok={rules.digit} text="At least one number" />
              <Rule ok={rules.special} text="At least one special character" />
            </ul>
          </div>

          {/* Confirm */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">Confirm password</label>
            <div className="relative">
              <input
                type={show2 ? "text" : "password"}
                className={`input pr-24 ${pw2 && !pwMatch ? "ring-1 ring-red-500/40" : ""}`}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
                maxLength={72}
                required
              />
              <button
                type="button"
                onClick={() => setShow2((v) => !v)}
                className="absolute inset-y-0 right-0 my-1 mr-1 rounded px-3 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20"
                aria-label={show2 ? "Hide password" : "Show password"}
              >
                {show2 ? "Hide" : "Show"}
              </button>
            </div>
            {pw2 && !pwMatch && (
              <p className="mt-1 text-xs text-red-300">Passwords do not match.</p>
            )}
          </div>

          <button
            className="btn-primary w-full"
            disabled={loading || !pwValid || !pwMatch}
            type="submit"
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>

        {/* Footer hint */}
        <p className="mt-4 text-xs text-slate-400">
          If this wasn’t you, ignore the email and your password will remain unchanged.
        </p>
      </section>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={ok ? "text-emerald-300" : "text-slate-400"}>
      {ok ? "✓" : "•"} {text}
    </li>
  );
}
